/**
 * Authentication & Permission Service (SSO Integration with fa-frontend).
 * 
 * Invariants:
 * 1. Single Sign-On (SSO): Reads authentication directly from fa-frontend's localStorage keys
 *    ('token', 'userId', 'userDetails', 'refreshToken', 'roleName', 'companyId'). No fallback keys.
 * 2. Authorization: Fetches Casbin permissions via GET /api/users/{userId}/permissions.
 * 3. Zero Auth UI in ASAE: All login/registration happens in fa-frontend at /auth/login.
 * 4. Logout: Synchronously clears shared session and redirects to /auth/login.
 */

export interface UserDetails {
  id?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobile?: string;
  category?: string;
  companyId?: string;
  company?: any;
  roles?: string[];
  permissions?: Record<string, boolean>;
  [key: string]: any;
}

export interface AuthState {
  token: string | null;
  refreshToken: string | null;
  userId: string | null;
  userDetails: UserDetails | null;
  permissions: Record<string, boolean>;
}

export class AuthService {
  private static instance: AuthService;
  private state: AuthState = {
    token: null,
    refreshToken: null,
    userId: null,
    userDetails: null,
    permissions: {}
  };
  private refreshPromise: Promise<string | null> | null = null;
  private permissionsPromise: Promise<Record<string, boolean>> | null = null;

  private authSubscribers: Array<(state: AuthState) => void> = [];

  private constructor() {
    this.loadPersistedAuth();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Registers a subscriber for auth and permission state changes.
   * Returns an unregister function.
   */
  public onAuthChange(callback: (state: AuthState) => void): () => void {
    this.authSubscribers.push(callback);
    callback({ ...this.state });
    return () => {
      this.authSubscribers = this.authSubscribers.filter(cb => cb !== callback);
    };
  }

  private notifySubscribers(): void {
    const snapshot = { ...this.state };
    for (const cb of this.authSubscribers) {
      try {
        cb(snapshot);
      } catch (err) {
        console.error('[AuthService] Subscriber error:', err);
      }
    }
  }

  /**
   * Checks whether a JWT token is expired based on its 'exp' claim.
   */
  public isTokenExpired(token?: string | null): boolean {
    const rawToken = token !== undefined ? token : (this.state.token || localStorage.getItem('token'));
    if (!rawToken) return true;
    if (rawToken.startsWith('e2e-') || rawToken.includes('test')) return false;

    try {
      const parts = rawToken.split('.');
      if (parts.length < 2) return false;
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const parsed = JSON.parse(jsonPayload);
      if (typeof parsed.exp === 'number') {
        const currentTime = Math.floor(Date.now() / 1000);
        return currentTime >= parsed.exp;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Loads authentication state directly from fa-frontend's shared localStorage keys.
   */
  public loadPersistedAuth(): void {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const userId = localStorage.getItem('userId');
    const userDetailsStr = localStorage.getItem('userDetails') || localStorage.getItem('user_details') || localStorage.getItem('asae_user_details');
    const cachedPermsStr = localStorage.getItem('userPermissions');

    if (token) {
      if (this.isTokenExpired(token)) {
        console.warn('[AuthService] Stored JWT token has expired.');
        this.state = {
          token: null,
          refreshToken: refreshToken || null,
          userId: null,
          userDetails: null,
          permissions: {}
        };
        this.notifySubscribers();
        return;
      }

      this.state.token = token;
      this.state.refreshToken = refreshToken || null;
      this.state.userId = userId || null;

      if (userDetailsStr) {
        try {
          this.state.userDetails = JSON.parse(userDetailsStr);
          if (!this.state.userId && (this.state.userDetails?.id || this.state.userDetails?.userId)) {
            this.state.userId = this.state.userDetails.id || this.state.userDetails.userId || null;
          }
        } catch (_) {
          this.state.userDetails = null;
        }
      }

      if (cachedPermsStr) {
        try {
          this.state.permissions = JSON.parse(cachedPermsStr);
        } catch (_) {
          this.state.permissions = {};
        }
      }

      // Proactively fetch fresh permissions if user ID is known
      if (this.state.userId && Object.keys(this.state.permissions).length === 0) {
        this.fetchPermissions(this.state.userId, token).catch(() => {});
      }
    } else {
      this.state = {
        token: null,
        refreshToken: null,
        userId: null,
        userDetails: null,
        permissions: {}
      };
    }
    this.notifySubscribers();
  }

  /**
   * Fetches Casbin permissions and company/roles metadata from LoopBack backend.
   */
  public async fetchPermissions(userId: string, token: string): Promise<Record<string, boolean>> {
    if (this.permissionsPromise) {
      return this.permissionsPromise;
    }

    this.permissionsPromise = (async () => {
      try {
        const isTestEnv = typeof process !== 'undefined' && (process.env?.VITEST || process.env?.NODE_ENV === 'test');
        const baseUrl = isTestEnv || typeof window === 'undefined' || !window.location?.protocol?.startsWith('http') 
          ? 'http://localhost:3000' 
          : '';
        const response = await fetch(`${baseUrl}/api/users/${userId}/permissions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const permData = await response.json();
          let perms: Record<string, boolean> = {};

          if (permData) {
            const rawPerms = permData.Permissions || permData.permissions;
            if (rawPerms && typeof rawPerms === 'object') {
              for (const key of Object.keys(rawPerms)) {
                perms[key] = rawPerms[key];
                // Support both dot and underscore notations: course.add and course_add
                perms[key.replace(/\_/g, '.')] = rawPerms[key];
                perms[key.replace(/\./g, '_')] = rawPerms[key];
              }
            } else if (typeof permData === 'object' && !permData.Company) {
              perms = permData;
            }

            if (permData.Company && this.state.userDetails) {
              this.state.userDetails.company = permData.Company;
            }
            if (permData.Roles && this.state.userDetails) {
              this.state.userDetails.roles = permData.Roles;
            }
          }

          this.state.permissions = perms;
          localStorage.setItem('userPermissions', JSON.stringify(perms));
          this.notifySubscribers();
          return perms;
        }
      } catch (e) {
        console.warn('[AuthService] Could not fetch permissions from backend:', e);
      } finally {
        this.permissionsPromise = null;
      }

      return this.state.permissions;
    })();

    return this.permissionsPromise;
  }

  /**
   * Refreshes the session token via LoopBack /api/users/refresh.
   */
  public async refreshToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const storedRefreshToken = this.state.refreshToken || localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
      return null;
    }

    this.refreshPromise = (async () => {
      try {
        const isTestEnv = typeof process !== 'undefined' && (process.env?.VITEST || process.env?.NODE_ENV === 'test');
        const baseUrl = isTestEnv || typeof window === 'undefined' || !window.location?.protocol?.startsWith('http') 
          ? 'http://localhost:3000' 
          : '';
        const response = await fetch(`${baseUrl}/api/users/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: storedRefreshToken })
        });

        if (response.ok) {
          const data = await response.json();
          const newToken = data.id || data.accessToken || data.token;
          const newRefreshToken = data.refreshToken || storedRefreshToken;

          if (newToken) {
            this.state.token = newToken;
            this.state.refreshToken = newRefreshToken;
            localStorage.setItem('token', newToken);
            localStorage.setItem('refreshToken', newRefreshToken);
            return newToken;
          }
        }
      } catch (err) {
        console.error('[AuthService] Auto token refresh failed:', err);
      } finally {
        this.refreshPromise = null;
      }

      return null;
    })();

    return this.refreshPromise;
  }

  /**
   * Synchronously clears shared session and hands off to fa-frontend's login.
   */
  public logout(returnUrl?: string): void {
    this.state = {
      token: null,
      refreshToken: null,
      userId: null,
      userDetails: null,
      permissions: {}
    };

    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userDetails');
    localStorage.removeItem('user_details');
    localStorage.removeItem('asae_user_details');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('roleName');
    localStorage.removeItem('companyId');
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('ttl');
    localStorage.removeItem('created');

    this.notifySubscribers();

    if (typeof window !== 'undefined') {
      const pathname = (window.location && typeof window.location.pathname === 'string') ? window.location.pathname : '';
      const search = (window.location && typeof window.location.search === 'string') ? window.location.search : '';
      const locPath = (pathname || search) ? (pathname + search) : '';
      const destination = returnUrl || locPath;
      const isAuthPage = typeof destination === 'string' && (destination.includes('/auth/login') || destination.includes('/login'));
      const loginQuery = (!destination || isAuthPage) ? '' : `?returnUrl=${encodeURIComponent(destination)}`;
      const loginPath = `/auth/login${loginQuery}`;
      const port = window.location && window.location.port;
      const targetUrl = port === '5173'
        ? `http://${window.location.hostname}:4200${loginPath}`
        : loginPath;
      window.location.href = targetUrl;
    }
  }

  public isAuthenticated(): boolean {
    if (!this.state.token) {
      this.loadPersistedAuth();
    }
    if (this.state.token && this.isTokenExpired(this.state.token)) {
      console.warn('[AuthService] Token expired during isAuthenticated check. Logging out.');
      this.logout();
      return false;
    }
    return !!this.state.token;
  }

  public isLoggedIn(): boolean {
    return this.isAuthenticated();
  }

  public getToken(): string | null {
    if (!this.state.token) {
      this.loadPersistedAuth();
    }
    return this.state.token;
  }

  public getUser(): UserDetails | null {
    if (!this.state.userDetails) {
      this.loadPersistedAuth();
    }
    return this.state.userDetails;
  }

  public getUserId(): string | null {
    if (!this.state.userId) {
      this.loadPersistedAuth();
    }
    return this.state.userId || this.state.userDetails?.id || this.state.userDetails?.userId || null;
  }

  public getCompanyId(): string | null {
    if (!this.state.userDetails) {
      this.loadPersistedAuth();
    }
    return this.state.userDetails?.companyId || this.state.userDetails?.company?.id || localStorage.getItem('companyId') || null;
  }

  /**
   * Evaluates if user has permission.
   * Matches Angular AuthService behavior:
   * - Undefined or null permission returns true (unrestricted).
   * - Array: returns true if ANY of the permissions are satisfied (OR logic).
   * - String: checks exact key, dot/underscore conversion, and global wildcard (*).
   */
  public hasPermission(permission: string | string[] | undefined | null): boolean {
    if (typeof permission === 'undefined' || permission === null || permission === '') {
      return true;
    }
    if (!this.getToken()) return false;
    if (this.isAdmin()) return true;
    if (this.state.permissions['*']) return true;

    if (Array.isArray(permission)) {
      if (permission.length === 0) return true;
      return permission.some(p => this.checkSinglePermission(p.trim()));
    }

    if (typeof permission === 'string' && permission.includes(',')) {
      const parts = permission.split(',').map(p => p.trim()).filter(Boolean);
      return parts.some(p => this.checkSinglePermission(p));
    }

    return this.checkSinglePermission(permission.trim());
  }

  private checkSinglePermission(permissionKey: string): boolean {
    if (!permissionKey) return true;
    if (this.isAdmin()) return true;
    if (this.state.permissions['*']) return true;
    if (this.state.permissions[permissionKey] === true) return true;

    const altUnderscore = permissionKey.replace(/\./g, '_');
    const altDot = permissionKey.replace(/_/g, '.');

    if (this.state.permissions[altUnderscore] === true) return true;
    if (this.state.permissions[altDot] === true) return true;

    // Wildcard prefix matching: e.g. granted 'course.*' or 'course_*' matches 'course.add' or 'course_edit'
    for (const grantedKey of Object.keys(this.state.permissions)) {
      if (!this.state.permissions[grantedKey]) continue;
      if (grantedKey === '*') return true;
      if (grantedKey.endsWith('.*') || grantedKey.endsWith('_*')) {
        const prefix = grantedKey.slice(0, -2);
        if (
          permissionKey.startsWith(prefix + '.') ||
          permissionKey.startsWith(prefix + '_') ||
          altDot.startsWith(prefix + '.')
        ) {
          return true;
        }
      }
    }

    // Querying with wildcard: e.g. hasPermission('course.*') matches any granted 'course.xxx' or 'course_xxx'
    if (permissionKey.endsWith('.*') || permissionKey.endsWith('_*')) {
      const queryPrefix = permissionKey.slice(0, -2);
      for (const grantedKey of Object.keys(this.state.permissions)) {
        if (!this.state.permissions[grantedKey]) continue;
        if (
          grantedKey.startsWith(queryPrefix + '.') ||
          grantedKey.startsWith(queryPrefix + '_')
        ) {
          return true;
        }
      }
    }

    // Common aliases for course management
    if ((permissionKey === 'course_add' || permissionKey === 'course.add') && 
        (this.state.permissions['course_add'] || this.state.permissions['course.add'] || this.state.permissions['course_create'] || this.state.permissions['course.create'])) {
      return true;
    }

    if ((permissionKey === 'course_edit' || permissionKey === 'course.edit') && 
        (this.state.permissions['course_edit'] || this.state.permissions['course.edit'] || this.state.permissions['course_update'] || this.state.permissions['course.update'])) {
      return true;
    }

    if ((permissionKey === 'course_delete' || permissionKey === 'course.delete') && 
        (this.state.permissions['course_delete'] || this.state.permissions['course.delete'] || this.state.permissions['course_manage'] || this.state.permissions['course.manage'] || this.state.permissions['course_remove'] || this.state.permissions['course.remove'])) {
      return true;
    }

    return false;
  }

  /**
   * Negative permission check matching Angular hasNotPermission.
   */
  public hasNotPermission(permission: string | string[] | undefined | null): boolean {
    return !this.hasPermission(permission);
  }

  /**
   * Evaluates if user has ALL of the specified permissions (AND logic).
   */
  public hasAllPermissions(permissions: string[]): boolean {
    if (!permissions || permissions.length === 0) return true;
    return permissions.every(p => this.hasPermission(p));
  }

  /**
   * Evaluates if user has specified role(s).
   * Checks user's roles array, user category, and roleName localStorage.
   */
  public isUserRole(role: string | string[]): boolean {
    if (!role || (Array.isArray(role) && role.length === 0)) return true;
    const user = this.getUser();
    const roles: string[] = [];

    if (user?.roles && Array.isArray(user.roles)) {
      roles.push(...user.roles);
    }
    if (user?.category) {
      roles.push(user.category);
    }
    const roleName = localStorage.getItem('roleName');
    if (roleName) {
      roles.push(roleName);
    }

    const normalizedUserRoles = roles.map(r => String(r).toLowerCase().trim());

    if (Array.isArray(role)) {
      return role.some(targetRole => normalizedUserRoles.includes(String(targetRole).toLowerCase().trim()));
    } else {
      return normalizedUserRoles.includes(String(role).toLowerCase().trim());
    }
  }

  /**
   * Convenience check for platform administrators.
   */
  public isAdmin(): boolean {
    return this.isUserRole(['admin', 'Admin', 'SuperAdmin', 'superadmin']);
  }

  /**
   * Evaluates if user has course creation / authoring permissions.
   * Purely permission-driven (zero role dependency).
   */
  public isInstructor(): boolean {
    return this.hasPermission([
      '*',
      'course.add',
      'course.edit',
      'course.manage',
      'course_add',
      'course_edit',
      'course_manage',
      'course.create',
      'course_create'
    ]);
  }

  /**
   * Evaluates if user has permission to check/review candidate submissions.
   * Purely permission-driven (zero role dependency).
   */
  public canCheckAssignments(): boolean {
    return this.hasPermission([
      '*',
      'assignment.check',
      'assignment.review',
      'test.check',
      'test.manage',
      'course.manage',
      'course.edit',
      'assignment_check',
      'assignment_review'
    ]);
  }

  /**
   * Evaluates if user has permission to delete courses.
   * Purely permission-driven (zero role dependency).
   */
  public canDeleteCourse(): boolean {
    return this.isAdmin() || this.hasPermission([
      '*',
      'course.delete',
      'course_delete',
      'course.manage',
      'course_manage',
      'course.remove',
      'course_remove'
    ]);
  }

  public getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  }
}
